import React from 'react';
import {
	Alert,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import {
	getUserBasicInfo,
	handleError,
	handleSuccess,
	handleWarning,
	isEmptyOrNull,
} from '../../helpers/utils';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router';
import MultipleFileUpload from './MultipleFileUpload';
import { useSelector } from 'react-redux';
import { createTicket } from '../../actions/tickets';
import { ref, getStorage, uploadBytes } from 'firebase/storage';
import { app } from '../../firebase/firebase-config';
import { bucketRef } from '../../firebase/credentials';

const initialForm = {
	type: '',
	title: '',
	description: '',
	status: 'Reportado',
};

export const NewTicketForm = () => {
	// Define state to know if it's loading or not
	const [isLoading, setIsLoading] = useState(false);

	// User
	const { user } = useSelector((state) => state);

	// Files
	const [files, setFiles] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [uploadError, setUploadError] = useState(null);

	// Handle file change
	const handleFileChange = (event) => {
		setFiles([...files, ...event.target.files]);
	};

	// Handle upload files
	const handleUploadTicketFiles = async (newTicketId) => {
		setUploading(true);
		setUploadError(null);

		const storageRef = getStorage(app, bucketRef);
		try {
			await Promise.all(
				files.map(async (file) => {
					const fileRef = ref(
						storageRef,
						`tickets/${newTicketId}/${file.name}`
					);
					await uploadBytes(fileRef, file);
				})
			);

			setProgress(100);
		} catch (error) {
			setUploadError(error.message);
		} finally {
			setUploading(false);
			setFiles([]);
		}
	};

	// Form
	const [form, setForm] = useState(initialForm);

	// Navigation
	const navigate = useNavigate();

	// Go to path
	const goToOrdersTable = () => {
		const path = `/dashboard/ordenes`;
		localStorage.setItem('currentPathPilu', path);
		navigate(path);
	};

	const handleValidateForm = () => {
		if (isEmptyOrNull(form.type)) return false;
		if (
			isEmptyOrNull(form.title) ||
			whichColorLength(30, 50, form.title.length, true)
		)
			return false;
		if (
			isEmptyOrNull(form.description) ||
			whichColorLength(200, 2000, form.description.length, true)
		)
			return false;

		return true;
	};

	const handleResetForm = () => {
		setForm(initialForm);
	};

	// Handle submit
	const handleSubmit = () => {
		// Set loading state to true
		setIsLoading(true);

		if (handleValidateForm()) {
			handleWarning('El formulario no está completado correctamente');
		} else {
			// Register new order
			createTicket({ ...form, reported_by: getUserBasicInfo(user) })
				.then((newTicket) => {
					// Feedback success
					handleSuccess('Ticket creado correctamente.');
					// Upload files to the new ticket's folder
					handleUploadTicketFiles(newTicket.id)
						.then(() => {
							handleSuccess('Archivos subidos correctamente');
							handleResetForm();
						})
						.catch((error) => {
							handleError(error, 'Error al subir los archivos');
						});
				})
				.catch((error) => {
					handleError(error, 'Error al intentar crear el ticket.');
				})
				.finally(() => {
					// Remove loading state
					setIsLoading(false);
				});
		}
	};

	const whichColorLength = (min, max, current, justBoolean = false) => {
		if (current === 0 || current < min) return justBoolean ? false : 'red';
		if (current >= min && current <= max)
			return justBoolean ? true : 'green';
	};

	const CharsLength = ({ min, max, item }) => (
		<small style={{ margin: 0 }}>
			<span
				style={{
					color: whichColorLength(min, max, form[item].length),
				}}
			>
				Caracteres mínimos: {form[item].length}/{min}
			</span>
		</small>
	);

	const handleUpdateForm = (item, value) => {
		setForm({
			...form,
			[item]: value,
		});
	};

	const setMinMaxLengthsProps = (min, max) => ({
		minLength: min,
		maxLength: max,
	});

	const IncidentSelect = () => (
		<FormControl fullWidth required>
			<InputLabel id='incident-label'>Tipo de Incidencia</InputLabel>
			<Select
				labelId='incident-label'
				id='incident-label'
				defaultValue=''
				value={form?.type}
				required
				fullWidth
				onChange={({ target }) =>
					handleUpdateForm('type', target.value)
				}
				label='Tipo de Incidencia'
				placeholder='Seleccione el tipo de incidente al cual está relacionado este ticket'
			>
				<MenuItem value='' disabled>
					Selecciona el tipo de incidencia
				</MenuItem>
				<MenuItem value='error'>
					<Tooltip
						placement='right-end'
						title='Problemas o fallos en el funcionamiento del software que impiden que se realice una tarea específica. Pueden incluir errores de programación, fallos de lógica, problemas de rendimiento, etc.'
					>
						<span>Error de Software</span>
					</Tooltip>
				</MenuItem>
				<MenuItem value='feature'>
					<Tooltip
						placement='right-end'
						title='Solicitudes de los usuarios para agregar una nueva característica o funcionalidad al software existente. Puede ser una nueva capacidad, una mejora de una función existente o una solicitud para agregar una integración con otro sistema.'
					>
						<span>Solicitud de Nueva Funcionalidad</span>
					</Tooltip>
				</MenuItem>
				<MenuItem value='enhancement'>
					<Tooltip
						placement='right-end'
						title='Problemas relacionados con la velocidad, eficiencia o capacidad de respuesta del software. Los usuarios pueden informar sobre funciones que son lentas o que consumen demasiados recursos.'
					>
						<span>Mejora de Rendimiento</span>
					</Tooltip>
				</MenuItem>
			</Select>
		</FormControl>
	);

	return (
		<div style={{ padding: '25px' }}>
			<Stack
				direction='column'
				alignItems='flex-start'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 10px 0',
					width: '100%',
				}}
			>
				<Typography variant='h4' gutterBottom>
					Reporte de incidencia en el sistema
				</Typography>
				<Typography variant='p' gutterBottom>
					Complete el formulario para reportar un nuevo "ticket"
					(problema) relacionado al sistema PILU. <br />
					<br />
					<Alert severity='warning'>
						<b>
							<u>
								Para que el ticket sea válido y útil se debe de
								ser muy claro y conciso en su descripción, de lo
								contrario no se tomará en cuenta.{' '}
							</u>
						</b>
					</Alert>
				</Typography>
			</Stack>
			<hr />
			<Grid container spacing={2} className='mt-2'>
				<Grid item xs={12}>
					<IncidentSelect />
				</Grid>

				<Grid item xs={12}>
					<TextField
						required
						fullWidth
						label='Título'
						placeholder='Breve título del problema'
						value={form?.title}
						onChange={({ target }) =>
							handleUpdateForm('title', target.value)
						}
						inputProps={setMinMaxLengthsProps(30, 50)}
					/>
					<CharsLength min={30} max={50} item='title' />
				</Grid>

				<Grid item xs={12}>
					<TextField
						required
						style={{ margin: '5px 0 0 0' }}
						fullWidth
						margin='normal'
						label='Descripción'
						placeholder='Explique explícitamente el problema actual. Entre más detalle mejor.'
						multiline
						rows={3}
						value={form?.description}
						onChange={({ target }) =>
							handleUpdateForm('description', target.value)
						}
						onBlur={({ target }) =>
							handleUpdateForm('description', target.value.trim())
						}
						inputProps={setMinMaxLengthsProps(200, 2000)}
					/>
					<CharsLength min={200} max={2000} item='description' />
				</Grid>

				<Grid item xs={12}>
					Sube evidencia útil. <br /> <br />
					<MultipleFileUpload
						handleFileChange={handleFileChange}
						files={files}
						setFiles={setFiles}
						uploading={uploading}
						progress={progress}
						uploadError={uploadError}
					/>
				</Grid>
			</Grid>
			<Grid container spacing={2} className='mt-2'>
				<Grid item xs={6}>
					<LoadingButton
						fullWidth
						size='large'
						color='error'
						onClick={goToOrdersTable}
						variant='contained'
						loading={false}
					>
						Cancelar y salir
					</LoadingButton>
				</Grid>

				<Grid item xs={6}>
					<LoadingButton
						fullWidth
						size='large'
						variant='contained'
						loading={isLoading}
						style={{ backgroundColor: '#143f6c' }}
						onClick={handleSubmit}
					>
						Crear ticket
					</LoadingButton>
				</Grid>
			</Grid>
		</div>
	);
};
